import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import {useEffect, useId, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {
    ModalDialog,
    ModalLauncher,
    ModalPanel,
} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useMutation} from "@khan/data-gql";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl} from "@khan/urls";

import InnerBirthdayPicker from "#components/birthday-picker/birthday-picker.tsx";
import {isDateOver18} from "#shared-package/is-over-18.ts";

import {FREE_FOR_TEACHERS_SAVE_ELIGIBILITY_MUTATION} from "./free-for-teachers-eligibility-form.tsx";
import {FREE_FOR_TEACHERS_ENROLL_MUTATION} from "./free-for-teachers-enroll.tsx";
import useFreeForTeachersTerms from "./use-free-for-teachers-terms.ts";

type Props = {
    // Whether the popup is shown or not.
    opened: boolean;

    // Callback to run when the popup is told to close. Use this to close the
    // opened prop to false.
    onClose: (success?: boolean) => void;
};

export function TeacherToolsSignupPopup(props: Props): React.ReactElement {
    const {opened, onClose} = props;

    const dialogTitleId = useId();
    const [birthday, setBirthday] = useState("");
    const [birthdayError, setBirthdayError] = useState("");
    const [generalError, setGeneralError] = useState("");
    const [loading, setLoading] = useState(false);
    const [enrollUser] = useMutation(FREE_FOR_TEACHERS_ENROLL_MUTATION);
    const [saveEligibility] = useMutation(
        FREE_FOR_TEACHERS_SAVE_ELIGIBILITY_MUTATION,
    );
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const termsUrl = useFreeForTeachersTerms();

    useEffect(() => {
        if (opened) {
            publishAnalyticsEvent({
                eventType: "AiTeacherToolsSignupPopupViewedTI",
                eventSchemaVersion: 1,
            });
        }
    }, [publishAnalyticsEvent, opened]);

    const modalHeader = (
        <View style={styles.header}>
            <HeadingLarge id={dialogTitleId} testId="frictionless-header">
                {t`Khanmigo for Teachers is now FREE!`}
            </HeadingLarge>
            <LabelSmall style={styles.subHeading}>
                {t`Our AI-powered teaching assistant makes your prep time both
                faster and better.`}
            </LabelSmall>
        </View>
    );

    const modalContent = (
        <View style={styles.content}>
            {/* Birthday Picker */}
            <LabelMedium style={{color: color.offBlack}}>
                {t`What is your date of birth?`}
            </LabelMedium>
            <Strut size={spacing.small_12} />
            <div className="birthdate-picker">
                <InnerBirthdayPicker
                    style={styles.birthdayPicker}
                    dropdownStyle={styles.birthdayPickerDropdown}
                    onChange={(date) => {
                        const newBirthday = date || "";
                        setBirthday(newBirthday);

                        setBirthdayError(
                            isDateOver18(newBirthday)
                                ? ""
                                : t`You are not eligible for Khanmigo for Teachers`,
                        );
                    }}
                />
                {birthdayError && (
                    <LabelMedium style={styles.formErrorText}>
                        {birthdayError}
                    </LabelMedium>
                )}
            </div>

            {/* Supplemental terms */}
            <LabelSmall style={styles.terms}>
                <Trans>
                    By opting to receive Khanmigo for Teachers, you agree to the{" "}
                    agree to the{" "}
                    <Link href={termsUrl} target="_blank">
                        Khanmigo Teacher Tools supplement
                    </Link>
                    . You confirm that you qualify for this type of account
                    access as a teacher.
                </Trans>
            </LabelSmall>

            {generalError && (
                <LabelMedium style={styles.formErrorText}>
                    {generalError}
                </LabelMedium>
            )}
        </View>
    );

    const modalFooter = (
        // TODO(davemcalpine): get the correct padding in there 32px
        <Button
            size="large"
            style={styles.cta}
            onClick={async () => {
                publishAnalyticsEvent({
                    eventType: "AiTeacherToolsSignupPopupSubmitClickedTI",
                    eventSchemaVersion: 1,
                });

                setGeneralError("");
                setLoading(true);

                const eligibilityResult = await saveEligibility({
                    teacherRoleOp: "ADD",
                    birthdate: birthday,
                });

                if (eligibilityResult.status === "error") {
                    setLoading(false);
                    setGeneralError(
                        t`Something went wrong enabling Khanmigo for Teachers. Please try again.`,
                    );
                    return;
                }

                const enrollResult = await enrollUser({
                    termsUrl,
                    // Implied by clicking the submit button
                    userAcceptedTerms: true,
                    countryCode: RequestInfo.remoteCountry,
                });

                setLoading(false);

                if (enrollResult.status === "success") {
                    onClose(true);
                    return;
                }

                // If we're here, we have an error
                if (enrollResult.status === "error") {
                    setGeneralError(
                        t`Something went wrong enabling Khanmigo for Teachers. Please try again.`,
                    );
                }
            }}
            disabled={!!birthdayError || !birthday || loading}
        >
            {t`Get Khanmigo for Teachers`}
        </Button>
    );

    return (
        <ModalLauncher
            opened={opened}
            onClose={onClose}
            backdropDismissEnabled={false}
            modal={
                <ModalDialog
                    style={styles.dialog}
                    aria-labelledby={dialogTitleId}
                >
                    <ModalPanel
                        closeButtonVisible={true}
                        header={modalHeader}
                        content={modalContent}
                        footer={modalFooter}
                    />
                </ModalDialog>
            }
        />
    );
}

const styles = StyleSheet.create({
    dialog: {
        overflow: "hidden",
        [mediaQueries.lgOrLarger]: {
            width: 540,
            height: 575,
        },
        [mediaQueries.mdOrSmaller]: {
            width: 320,
            height: 760,
            padding: 0,
        },
    },
    header: {
        textAlign: "center",

        [mediaQueries.lgOrLarger]: {
            height: 260,
            padding: `104px 93px`,
            backgroundImage: cssUrl(
                "/images/khanmigo/frictionless_teacher_tools_desktop_bg.png",
            ),
        },
        [mediaQueries.mdOrSmaller]: {
            height: 292,
            padding: `104px ${spacing.large_24}px`,
            backgroundImage: cssUrl(
                "/images/khanmigo/frictionless_teacher_tools_mobile_bg.png",
            ),
        },
        backgroundSize: "contain",
    },
    subHeading: {
        padding: `12px ${spacing.small_12}px`,
    },
    birthdayPicker: {
        [mediaQueries.lgOrLarger]: {
            flexDirection: "row",
        },
        [mediaQueries.mdOrSmaller]: {
            flexDirection: "column",
        },
    },
    birthdayPickerDropdown: {
        [mediaQueries.mdOrSmaller]: {
            width: "100%",
        },
    },
    content: {
        // We're unable to override the default padding of the ModalPanel
        // content (32px). So to achieve the desired padding, we're adding a
        // negative margin to the content.
        marginTop: `-${spacing.xSmall_8}px`,

        [mediaQueries.smOrSmaller]: {
            // At this screen size the modal has padding of 16px, so we set
            // additional margin to bring the gutter back to 32px
            marginLeft: `${spacing.medium_16}px`,
            marginRight: `${spacing.medium_16}px`,
        },
    },
    terms: {
        color: color.offBlack64,
        margin: `${spacing.large_24}px 0 0`,
    },
    formErrorText: {
        color: color.red,
        marginTop: spacing.small_12,
    },
    cta: {
        margin: `${spacing.small_12}px ${spacing.medium_16}px`,
        [mediaQueries.mdOrSmaller]: {
            width: "100%",
            padding: `${spacing.large_24}px 0`,
        },
    },
});
